import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Send, CheckCircle, AlertCircle, User, Mail, MessageSquare, StickyNote } from 'lucide-react';
import { api } from '../services/api';
import { useTranslation, Trans } from 'react-i18next';

export default function ContactForm({ onClose }: { onClose?: () => void }) {
    const { t } = (useTranslation as any)('landing');
    const [searchParams] = useSearchParams();
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState('');

    const [formData, setFormData] = useState({
        name: '',
        email: '',
        subject: searchParams.get('subject') || '',
        message: searchParams.get('message') || ''
    });

    // Update if params change (e.g. if component is already mounted)
    useEffect(() => {
        const subj = searchParams.get('subject');
        const msg = searchParams.get('message');
        if (subj || msg) {
            setFormData(prev => ({
                ...prev,
                subject: subj || prev.subject,
                message: msg || prev.message
            }));
        }
    }, [searchParams]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            await api.system.sendContact(formData);
            setSuccess(true);
            setTimeout(() => {
                if (onClose) onClose();
            }, 3000);
        } catch (err: any) {
            setError(err.message || t('contact_form.error_generic'));
        } finally {
            setLoading(false);
        }
    };

    const TransAny = Trans as any;

    if (success) {
        return (
            <div className="text-center p-12 flex flex-col items-center justify-center h-full min-h-[400px]">
                <div className="w-20 h-20 bg-green-100 text-green-600 rounded-full flex items-center justify-center mb-6 animate-bounce">
                    <CheckCircle size={40} />
                </div>
                <h3 className="text-2xl font-black text-slate-800 mb-2">{t('contact_form.success_title')}</h3>
                <p className="text-slate-500 max-w-xs mx-auto">
                    <TransAny
                        ns="landing"
                        i18nKey="contact_form.success_p"
                        values={{ email: formData.email }}
                        components={{ strong: <strong /> }}
                    />
                </p>
                <div className="mt-4 bg-yellow-50 text-yellow-700 text-xs p-3 rounded-lg border border-yellow-200">
                    {t('contact_form.spam_hint')}
                </div>
                <div className="mt-8 w-full max-w-xs h-1 bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-full bg-green-500 animate-[width_3s_ease-in-out_forwards]" style={{ width: '0%' }}></div>
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col h-full">
            {/* Header with Warm Brand Style */}
            <div className="bg-slate-900 px-8 py-6 border-b-4 border-yellow-400">
                <h2 className="text-2xl font-black text-white tracking-tight flex items-center gap-3">
                    <div className="p-2 bg-white/10 rounded-lg backdrop-blur-sm text-yellow-400">
                        <MessageSquare size={24} />
                    </div>
                    {t('contact_form.title')}
                </h2>
                <div className="text-slate-400 text-sm mt-2 ml-14">
                    <p>{t('contact_form.subtitle')}</p>
                    <p className="mt-1">
                        <TransAny i18nKey="contact_form.direct_email" ns="landing" t={t as any}>
                            You can also email us directly at <a href="mailto:contact@getpadelpartner.com" className="text-blue-400 hover:text-blue-300 font-medium transition-colors">contact@getpadelpartner.com</a>
                        </TransAny>
                    </p>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="p-8 space-y-5 flex-1 overflow-y-auto">
                {error && (
                    <div className="bg-red-50 text-red-600 p-4 rounded-xl flex items-center gap-3 text-sm font-medium border border-red-100 animate-shake">
                        <AlertCircle size={20} className="shrink-0" /> {error}
                    </div>
                )}

                <div className="space-y-4">
                    <div className="relative group">
                        <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5 ml-1">{t('contact_form.labels.name')}</label>
                        <div className="relative">
                            <div className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-blue-500 transition-colors">
                                <User size={18} />
                            </div>
                            <input
                                type="text"
                                required
                                placeholder={t('contact_form.placeholders.name')}
                                className="w-full bg-slate-50 border-2 border-slate-100 rounded-xl p-3 pl-12 outline-none focus:border-blue-500 focus:bg-white transition-all font-medium text-slate-700 placeholder-slate-400"
                                value={formData.name}
                                onChange={e => setFormData({ ...formData, name: e.target.value })}
                            />
                        </div>
                    </div>

                    <div className="relative group">
                        <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5 ml-1">{t('contact_form.labels.email')}</label>
                        <div className="relative">
                            <div className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-blue-500 transition-colors">
                                <Mail size={18} />
                            </div>
                            <input
                                type="email"
                                required
                                placeholder={t('contact_form.placeholders.email')}
                                className="w-full bg-slate-50 border-2 border-slate-100 rounded-xl p-3 pl-12 outline-none focus:border-blue-500 focus:bg-white transition-all font-medium text-slate-700 placeholder-slate-400"
                                value={formData.email}
                                onChange={e => setFormData({ ...formData, email: e.target.value })}
                            />
                        </div>
                    </div>

                    <div className="relative group">
                        <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5 ml-1">{t('contact_form.labels.subject')}</label>
                        <div className="relative">
                            <div className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-blue-500 transition-colors">
                                <StickyNote size={18} />
                            </div>
                            <input
                                type="text"
                                placeholder={t('contact_form.placeholders.subject')}
                                className="w-full bg-slate-50 border-2 border-slate-100 rounded-xl p-3 pl-12 outline-none focus:border-blue-500 focus:bg-white transition-all font-medium text-slate-700 placeholder-slate-400"
                                value={formData.subject}
                                onChange={e => setFormData({ ...formData, subject: e.target.value })}
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5 ml-1">{t('contact_form.labels.message')}</label>
                        <textarea
                            required
                            rows={4}
                            placeholder={t('contact_form.placeholders.message')}
                            className="w-full bg-slate-50 border-2 border-slate-100 rounded-xl p-4 outline-none focus:border-blue-500 focus:bg-white transition-all font-medium text-slate-700 placeholder-slate-400 resize-none"
                            value={formData.message}
                            onChange={e => setFormData({ ...formData, message: e.target.value })}
                        />
                    </div>
                </div>

                <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-gradient-to-r from-blue-600 to-blue-500 text-white font-bold py-4 rounded-xl hover:from-blue-700 hover:to-blue-600 transition-all shadow-lg shadow-blue-500/30 hover:scale-[1.02] active:scale-[0.98] flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed mt-4"
                >
                    {loading ? (
                        <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                        <>
                            <Send size={20} /> {t('contact_form.actions.send')}
                        </>
                    )}
                </button>
            </form>
        </div>
    );
}
